import React, { createContext, useContext, useEffect, useState } from "react";
import { StatusCode } from "../../components/Alert.tsx";
import RecipesController from '../../controllers/RecipesController.ts';
import { Recipe } from '../../models/recipes/recipe.ts';
import { RecipeSearchParam } from "../../models/searchParams/recipeSearchParam.ts";
import { TableSearchParam } from "../../models/searchParams/tableSearchParam.ts";
import { useAlert } from "../alertProvider.tsx";

type RecipesContextType = {
    isLoaded?: boolean;
    recipes: Recipe[];
    totalCount: number;
    tableSearchParam: TableSearchParam;
    setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
    searchParam: RecipeSearchParam;
    setSearchParam: (baseSearchParam: RecipeSearchParam) => void;
    deleteRecipe: (recipeID: number) => {};
};

const RecipesContext = createContext<RecipesContextType>({} as RecipesContextType);

export const RecipesContextProvider = ({ children }: { children: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<RecipeSearchParam>({} as RecipeSearchParam);
    const { showAlert } = useAlert();

    const fetchRecipes = async () => {
        try {
            setIsLoaded(false);
            const result = await RecipesController.get(tableSearchParam, searchParam);
            setRecipes(result.recipes);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('Error fetching recipes:', error);
        } finally {
            setIsLoaded(true);
        }
    };
    useEffect(() => {
        fetchRecipes();
    }, [tableSearchParam, searchParam]);

    const deleteRecipe = async (recipeID: number) => {
        try {
            const result = await RecipesController.delete(recipeID);
            if (result.notificationStatus == StatusCode.success)
                fetchRecipes();
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error deleting recipe:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const context: RecipesContextType = {
        isLoaded,
        recipes,
        totalCount,
        tableSearchParam,
        setTableSearchParam,
        searchParam,
        setSearchParam,
        deleteRecipe
    }

    return (
        <RecipesContext.Provider value={context}>
            {children}
        </RecipesContext.Provider>
    )
}

export const useRecipesContext = () => useContext(RecipesContext)
